import React from 'react';
import { WhatsappLogo, ShieldCheck, Warning, QrCode, ChatCircle, Lightning } from '@phosphor-icons/react';
import './WhatsAppMockup.css';

/**
 * Mockup pagina: laat zien hoe een WhatsApp-integratie in BillFac eruit kan zien.
 * Beslissingshulp voor de gebruiker — geen actuele functionaliteit nog.
 */
export default function WhatsAppMockup() {
  return (
    <div className="wa-mock" data-testid="whatsapp-mockup-page">
      <div style={{ maxWidth: 1400, margin: '0 auto 24px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1f2937', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <WhatsappLogo size={40} weight="fill" color="#25D366" />
          WhatsApp-integratie — Mockup
        </h1>
        <p style={{ color: '#6b7280', marginTop: 8 }}>
          Zo zou een WhatsApp Hub eruit zien als we het in BillFac bouwen. Beide opties hieronder vergeleken.
        </p>
      </div>

      <div className="wa-mock-grid">
        {/* ─── OPTIE A: QR-code (Baileys / WhatsApp Web) ────────────────── */}
        <div className="wa-mock-card" data-testid="qr-mockup">
          <div className="wa-mock-hdr">
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <QrCode size={20} weight="bold" />
              Optie A — QR-code (zoals WhatsApp Web)
            </span>
            <span style={{ fontSize: 11, background: 'rgba(255,255,255,.25)', padding: '2px 8px', borderRadius: 4 }}>GRATIS</span>
          </div>
          <div className="wa-mock-body">
            <div className="wa-qr-wrapper">
              <div className="wa-qr-box">
                <div className="wa-qr-img" />
                <div className="wa-pulse"><WhatsappLogo size={32} weight="fill" /></div>
              </div>
              <p style={{ textAlign: 'center', color: '#374151', fontSize: 13, maxWidth: 280 }}>
                <strong>Scan deze QR met je telefoon</strong><br/>
                <small style={{ color: '#6b7280' }}>
                  WhatsApp openen → Instellingen → Gekoppelde apparaten → Apparaat koppelen
                </small>
              </p>
            </div>
            <div className="wa-warn">
              <Warning size={14} weight="bold" style={{ marginRight: 4, verticalAlign: 'middle' }} />
              <strong>Let op:</strong> dit gebruikt een onofficieel protocol (Baileys). Tegen WhatsApp's voorwaarden — kans op account-blokkade.
            </div>
            <ul className="wa-steps" style={{ marginTop: 16 }}>
              <li>✅ Setup in 30 seconden</li>
              <li>✅ Geen verificatie nodig</li>
              <li>✅ Gebruikt je bestaande WhatsApp-nummer</li>
              <li style={{ color: '#dc2626' }}>❌ Account-ban risico (Meta detecteert)</li>
              <li style={{ color: '#dc2626' }}>❌ Sessie verloopt regelmatig → opnieuw scannen</li>
              <li style={{ color: '#dc2626' }}>❌ Niet aanbevolen voor SaaS-klanten</li>
            </ul>
          </div>
        </div>

        {/* ─── OPTIE B: Meta Cloud API (officieel) ───────────────────────── */}
        <div className="wa-mock-card" data-testid="cloud-mockup">
          <div className="wa-mock-hdr cloud">
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={20} weight="bold" />
              Optie B — Meta Cloud API (officieel)
            </span>
            <span style={{ fontSize: 11, background: 'rgba(255,255,255,.25)', padding: '2px 8px', borderRadius: 4 }}>1000 GRATIS/MND</span>
          </div>
          <div className="wa-mock-body">
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 12 }}>Setup-flow:</div>
              <ol style={{ fontSize: 13, color: '#374151', lineHeight: 2 }}>
                <li>📋 Maak Meta Business Manager account aan</li>
                <li>📞 Voeg een dedicated WhatsApp-nummer toe</li>
                <li>📑 Upload KBO/BTW voor bedrijfsverificatie</li>
                <li>⏳ Wacht 3-7 dagen op goedkeuring</li>
                <li>🔑 Plak <code style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: 4 }}>WHATSAPP_TOKEN</code> in BillFac</li>
                <li>✨ Klaar — verstuur officieel & schaalbaar</li>
              </ol>
            </div>
            <div className="wa-info" style={{ marginTop: 16 }}>
              <ShieldCheck size={14} weight="bold" style={{ marginRight: 4, verticalAlign: 'middle' }} />
              <strong>Officieel:</strong> Meta-gecertificeerd. 1000 gesprekken/maand gratis, daarna €0,03-€0,11 per gesprek.
            </div>
            <ul className="wa-steps" style={{ marginTop: 16 }}>
              <li>✅ Geen risico op account-ban</li>
              <li>✅ Hoge volumes mogelijk (10.000+ /maand)</li>
              <li>✅ Verzend factuur-PDF's, templates, betaallinks</li>
              <li>✅ Stabiele lange termijn werking</li>
              <li style={{ color: '#f59e0b' }}>⚠️ 3-7 dagen verificatie nodig</li>
              <li style={{ color: '#f59e0b' }}>⚠️ Vereist apart WhatsApp-nummer</li>
            </ul>
          </div>
        </div>
      </div>

      {/* ─── Hoe het eruit zou zien in BillFac ───────────────────────────── */}
      <div className="wa-cmp" data-testid="chat-mockup">
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, color: '#1f2937', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ChatCircle size={24} weight="duotone" color="#25D366" />
          Hoe het er in BillFac zou uitzien
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 0, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          {/* Chat-lijst */}
          <div style={{ background: '#f0f2f5', padding: 0 }}>
            <div style={{ padding: '14px 16px', background: '#25D366', color: 'white', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <WhatsappLogo size={18} weight="fill" /> WhatsApp Hub
            </div>
            {[
              { name: 'Jan Peeters (KL-00042)', last: 'Factuur 2025-1289 ontvangen, dankjewel!', unread: 0, ts: '14:32', avatar: '#3B82F6' },
              { name: 'BV A Reno', last: 'Wanneer komen de panelen?', unread: 2, ts: '13:58', avatar: '#F59E0B' },
              { name: 'Café De Hoop', last: 'Kan je factuur opnieuw sturen?', unread: 1, ts: '11:20', avatar: '#10B981' },
              { name: 'Steven De Vos', last: 'Ok, betaal vandaag', unread: 0, ts: 'gisteren', avatar: '#7C3AED' },
            ].map((c, i) => (
              <div key={i} style={{ padding: '12px 16px', display: 'flex', gap: 12, borderBottom: '1px solid #e9edef', background: i === 1 ? '#e7f3ff' : 'white', cursor: 'pointer' }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: c.avatar, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>
                  {c.name.split(' ').map(x => x[0]).slice(0, 2).join('')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 13, color: '#111827' }}>{c.name}</strong>
                    <small style={{ fontSize: 10, color: '#6b7280' }}>{c.ts}</small>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                    <span style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{c.last}</span>
                    {c.unread > 0 && <span style={{ background: '#25D366', color: 'white', borderRadius: 10, padding: '2px 8px', fontSize: 10, fontWeight: 700, marginLeft: 8 }}>{c.unread}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Chat-detail */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 20px', background: '#f0f2f5', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#F59E0B', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>BR</div>
              <div>
                <strong style={{ fontSize: 14, color: '#111827' }}>BV A Reno</strong>
                <div style={{ fontSize: 11, color: '#10B981' }}>● online — Klant sinds 2023</div>
              </div>
              <div style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
                📂 8 facturen · 💰 € 12.450 openstaand
              </div>
            </div>
            <div className="wa-chat-preview">
              <div className="wa-bubble wa-bubble-in"><strong>BV A Reno</strong>Hallo, ik heb een vraag over factuur 2025-1290 <small>13:54 ✓✓</small></div>
              <div className="wa-bubble wa-bubble-out">Goedemiddag! Even kijken... <small>13:55 ✓✓</small></div>
              <div className="wa-bubble wa-bubble-out">
                <div style={{ background: '#f0f2f5', borderRadius: 6, padding: 8, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 20 }}>📄</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>Factuur_2025-1290.pdf</div>
                    <small style={{ color: '#667781', fontSize: 10 }}>248 KB · BillFac</small>
                  </div>
                </div>
                Hier de factuur opnieuw — ik zie dat hij vervaldatum 15 juni heeft. <small>13:55 ✓✓</small>
              </div>
              <div className="wa-bubble wa-bubble-in"><strong>BV A Reno</strong>Wanneer worden de panelen geleverd? <small>13:58 ✓</small></div>
              <div style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #ddd6fe 100%)', border: '1px dashed #8B5CF6', borderRadius: 8, padding: 10, marginTop: 8, fontSize: 11, color: '#5B21B6' }}>
                <strong>🤖 AI-suggestie:</strong> &quot;Levering staat gepland voor maandag 16 juni tussen 09:00-12:00. Wenst u een specifiek tijdslot?&quot;
                <button style={{ marginLeft: 8, padding: '2px 8px', background: '#8B5CF6', color: 'white', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Verzend</button>
              </div>
            </div>
            <div style={{ padding: 12, background: '#f0f2f5', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, alignItems: 'center' }}>
              <button style={{ padding: '6px 10px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>📎 Bijlage</button>
              <button style={{ padding: '6px 10px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>📋 Sjabloon</button>
              <input style={{ flex: 1, padding: 8, borderRadius: 20, border: '1px solid #d1d5db', fontSize: 12 }} placeholder="Typ een bericht..." />
              <button style={{ width: 36, height: 36, borderRadius: '50%', background: '#25D366', color: 'white', border: 'none', cursor: 'pointer' }}>➤</button>
            </div>
          </div>
        </div>
        <div className="wa-ok">
          <Lightning size={14} weight="bold" style={{ marginRight: 4, verticalAlign: 'middle' }} />
          <strong>Bonus-feature:</strong> AI ziet de vraag → suggereert antwoord op basis van je agenda + voorraad. Eén klik → verzonden.
        </div>
      </div>

      {/* ─── Vergelijking ─────────────────────────────────────────────── */}
      <div className="wa-cmp" data-testid="comparison-table">
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16, color: '#1f2937' }}>📊 Vergelijking voor BillFac</h2>
        <table>
          <thead>
            <tr>
              <th></th>
              <th style={{ color: '#25D366' }}>Optie A — QR-code</th>
              <th style={{ color: '#1877F2' }}>Optie B — Cloud API</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Setup-tijd</td><td className="yes">30 seconden</td><td className="warn">3-7 dagen verificatie</td></tr>
            <tr><td>Kosten</td><td className="yes">€0 (gratis)</td><td className="yes">€0 voor 1000/maand, daarna €0,03-€0,11/conv</td></tr>
            <tr><td>Risico account-ban</td><td className="no">🔴 Hoog</td><td className="yes">🟢 Nul</td></tr>
            <tr><td>WhatsApp-nummer</td><td className="yes">Bestaand GSM</td><td className="warn">Apart business-nummer</td></tr>
            <tr><td>PDF-bijlagen versturen</td><td className="yes">Ja</td><td className="yes">Ja</td></tr>
            <tr><td>Templates / marketing</td><td className="warn">Beperkt</td><td className="yes">Volledig — pre-approved</td></tr>
            <tr><td>Geschikt voor SaaS klanten</td><td className="no">❌ Niet aan te raden</td><td className="yes">✅ Ja</td></tr>
            <tr><td>Onderhoudslast</td><td className="warn">Hoog (sessie verloopt)</td><td className="yes">Laag</td></tr>
            <tr><td>Lange termijn stabiliteit</td><td className="no">❌ Kan stuk bij Meta-update</td><td className="yes">✅ API-contract</td></tr>
          </tbody>
        </table>

        <div className="wa-actions">
          <button className="wa-btn wa-btn-qr" data-testid="choose-qr">Kies QR-modus (Baileys)</button>
          <button className="wa-btn wa-btn-cloud" data-testid="choose-cloud">Kies Cloud API (officieel)</button>
          <button className="wa-btn wa-btn-both" data-testid="choose-both">Bouw beide — gebruiker kiest zelf</button>
          <button className="wa-btn wa-btn-ghost" data-testid="choose-later">Misschien later</button>
        </div>
        <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 12 }}>
          Klik op een knop om je keuze door te geven (mockup — nog geen actie).
        </p>
      </div>
    </div>
  );
}
